import {
  GREGORIAN_CALENDAR_ID,
  HARPTOS_CALENDAR_ID,
  loadBundledCalendar,
  toCanonicalDay,
  type Calendar,
  type LeapRule,
} from '@dl/calendar';
import {
  DEFAULT_FACT_OWNER,
  DEFAULT_FACT_STATUS,
  type EntityState,
  type FactState,
  type FactValidity,
  type PredicateDeclaration,
  type TraitDeclaration,
  type TupleFieldKind,
  type ValueKind,
} from '@dl/shared';
import {
  assetField,
  badge,
  button as uiButton,
  calendarDatePicker,
  calendarEditor,
  colorPicker,
  dateField,
  validateWorkingCalendar,
  type CalendarCursor,
  type CalendarDatePickerMessages,
  type CalendarDatePickerOverlay,
  type CalendarEditorMessages,
  type DateFieldMessages,
  type CalendarIssue,
  type CalendarListEntry,
  type WorkingCalendar,
  type WorkingEra,
  type WorkingMonth,
  card as uiCard,
  collapse,
  emptyState,
  entityCard,
  entityIcon,
  formField,
  markdown,
  pageHeader,
  provenanceBadge,
  revealChip,
  sectionHeading,
  selectInput,
  statusPill,
  tabs,
  textArea,
  textInput,
  themeToggle,
  validityEditor,
  type BadgeTone,
  type ButtonVariant,
  type EntityIconRoot,
  type ProvenanceKind,
  type RevealState,
  type StatusKind,
} from '@dl/ui';
import {
  derived,
  div,
  each,
  h1,
  h2,
  h3,
  input,
  label,
  main,
  p,
  section,
  span,
  text,
  type Mountable,
  type Send,
  type Signal,
} from '@llui/dom';

import type { TraitEditorState, TraitEditorField, TupleEditorField } from './app.js';
import { campaignAccentSwatches, colorPickerMessages } from './color-swatches.js';
import {
  viewEntityFactRows,
  type FactRowHost,
} from './entities/fact-row-editor.js';
import { factRowKey } from './entities/fact-row-state.js';
import { t } from './i18n.js';
// Type-only imports (fully erased — they create NO runtime dependency on the
// huge app.ts module, so the gallery bundle stays lean). The live trait-editor
// specimen reuses the app's authoring state shapes; the build-preview is
// computed locally (importing the app's `buildTraitDefinitionOps` value would
// drag all of app.ts + its module-scope substrate into the gallery bundle).

/**
 * `/ui-gallery` — a llui-native component explorer for the `@dl/ui`
 * design-system primitives (#85).
 *
 * Why not Storybook: the repo deliberately has no Storybook — it uses
 * static HTML mocks for visual reference and ships its own llui signal
 * runtime. A gallery built on that same runtime is the honest "plays
 * well with llui" demonstration: every atom below is rendered exactly
 * as the app renders it, with the real CSS, the real tokens, and the
 * real theme switch. It doubles as living design-system docs.
 *
 * The route is in the production build (an always-rendered arm of the
 * app route `branch`, reachable without a campaign or a session — see
 * the public-route guard in `app.ts`) so CI's `vite build` + preview
 * can axe-scan it as a static surface.
 *
 * Structure: an `h1` page title, then two top-level `<section>`s
 * (Atoms / Molecules) each headed by an `h2`, and a `<section>` per
 * component headed by an `h3` — a clean, axe-`heading-order`-correct
 * landmark tree. Section + state labels flow through `t()`; the
 * component names stay literal monospace tokens (they are API names,
 * not UI copy, and pass the i18n English-sweep's lowercase guard).
 */

const ENTITY_ROOTS: readonly EntityIconRoot[] = [
  'location',
  'character',
  'faction',
  'event',
  'item',
  'lore',
];

const STATUS_KINDS: readonly StatusKind[] = [
  'canon',
  'pending',
  'draft',
  'hypothetical',
  'belief',
];

const REVEAL_STATES: readonly RevealState[] = ['public', 'partial', 'hidden', 'redacted'];

const PROVENANCE_KINDS: readonly ProvenanceKind[] = ['gm', 'llm', 'player', 'system'];

const BUTTON_VARIANTS: readonly ButtonVariant[] = ['primary', 'secondary', 'ghost', 'danger', 'icon'];

const BADGE_TONES: readonly BadgeTone[] = ['neutral', 'accent', 'info', 'warn', 'success'];

interface TabSpecimen {
  value: string;
  label: string;
  active: boolean;
  count?: number;
  disabled?: boolean;
}

/** The sample tablist + its rendered panels share this definition so the
 * tab triggers' `aria-controls` always resolve to a real panel. */
const TAB_SPECIMENS: readonly TabSpecimen[] = [
  { value: 'overview', label: t('entity.tab.overview'), active: true },
  { value: 'facts', label: t('entity.tab.facts'), active: false, count: 12 },
  { value: 'reveals', label: t('entity.tab.reveals'), active: false },
  { value: 'history', label: t('entity.tab.history'), active: false, disabled: true },
];

/** Translated label for a fact-status kind. `belief` is an owner, not a
 * status, in the catalog — so it resolves to the owner label. */
function statusLabel(kind: StatusKind): string {
  return kind === 'belief' ? t('fact.owner.belief') : t(`fact.status.${kind}`);
}

/** A small monospace caption naming the component (an API name, not UI copy). */
function componentName(name: string): Mountable {
  return span({ class: 'ui-gallery-component-name' }, [name]);
}

/** A labelled specimen cell — a swatch with its state caption beneath. */
function specimen(stateKey: string, node: Mountable): Mountable {
  return div({ class: 'ui-gallery-specimen' }, [
    div({ class: 'ui-gallery-specimen-stage' }, [node]),
    span({ class: 'ui-gallery-specimen-label' }, [text(t(stateKey))]),
  ]);
}

/** The "pending design review" flag — dogfoods the new `badge()` atom
 * (warn tone) with a leading ◷ non-colour glyph so the unreviewed status
 * reads without relying on colour (a11y), plus the literal "UNREVIEWED".
 * Kept as a helper so the flag is DRY across the unreviewed sections. */
function unreviewedBadge(): Mountable {
  return badge({
    tone: 'warn',
    testid: 'gallery-unreviewed-flag',
    children: [
      span({ class: 'ui-gallery-unreviewed-glyph', 'aria-hidden': 'true' }, ['◷ ']),
      text(t('ui_gallery.unreviewed')),
    ],
  });
}

/** One component section: an `h3` name, a description, and a row of
 * specimens. `reviewed` defaults to `true`; an UNREVIEWED component gets
 * a visible warn badge in its heading and a `data-reviewed="false"`
 * marker on the section (the existing reviewed atoms stay unbadged). */
function componentSection(
  name: string,
  descKey: string,
  specimens: Mountable[],
  reviewed = true,
): Mountable {
  const headingChildren: Mountable[] = [componentName(name)];
  if (!reviewed) headingChildren.push(unreviewedBadge());
  const descChildren: Mountable[] = [text(t(descKey))];
  if (!reviewed) {
    descChildren.push(span({ class: 'ui-gallery-unreviewed-note' }, [text(t('ui_gallery.unreviewed.note'))]));
  }
  return section(
    { class: 'ui-gallery-component', 'data-reviewed': reviewed ? 'true' : 'false' },
    [
      h3({ class: 'ui-gallery-component-heading' }, headingChildren),
      p({ class: 'ui-gallery-component-desc' }, descChildren),
      div({ class: 'ui-gallery-specimen-row' }, specimens),
    ],
  );
}

function atomsSection(): Mountable {
  return section({ class: 'ui-gallery-group', 'aria-labelledby': 'ui-gallery-atoms-heading' }, [
    h2({ id: 'ui-gallery-atoms-heading', class: 'ui-gallery-group-heading' }, [
      text(t('ui_gallery.atoms_heading')),
    ]),

    // entityIcon — one swatch per entity root.
    componentSection(
      'entityIcon',
      'ui_gallery.entity_icon.desc',
      ENTITY_ROOTS.map((root) =>
        specimen(
          `entity.root.${root}`,
          span({ class: 'ui-gallery-icon-tile', 'data-root': root }, [
            entityIcon(root, { size: 22 }),
          ]),
        ),
      ),
    ),

    // statusPill — the full canon spectrum.
    componentSection('statusPill', 'ui_gallery.status_pill.desc', [
      ...STATUS_KINDS.filter((k) => k !== 'belief').map((kind) =>
        specimen(
          `fact.status.${kind}`,
          statusPill({ kind, label: t(`fact.status.${kind}`), testid: `gallery-status-${kind}` }),
        ),
      ),
      specimen(
        'fact.owner.belief',
        statusPill({ kind: 'belief', label: t('fact.owner.belief'), testid: 'gallery-status-belief' }),
      ),
    ]),

    // revealChip — glyph-only + labelled, across visibility states.
    componentSection('revealChip', 'ui_gallery.reveal_chip.desc', [
      ...REVEAL_STATES.map((revealState) =>
        specimen(
          `ui_gallery.reveal.${revealState}`,
          revealChip({ state: revealState, label: t(`ui_gallery.reveal.${revealState}`) }),
        ),
      ),
      specimen(
        'ui_gallery.reveal.labelled',
        revealChip({
          state: 'public',
          label: t('ui_gallery.reveal.public'),
          text: t('reveal.chip.partial_short'),
        }),
      ),
    ]),

    // provenanceBadge — one per authoring source.
    componentSection(
      'provenanceBadge',
      'ui_gallery.provenance_badge.desc',
      PROVENANCE_KINDS.map((kind) =>
        specimen(
          `fact.author.${kind}`,
          provenanceBadge({ kind, label: t(`fact.author.${kind}`) }),
        ),
      ),
    ),

    // card — surface variants.
    componentSection('card', 'ui_gallery.card.desc', [
      specimen(
        'ui_gallery.state.default',
        uiCard({
          variant: 'default',
          padding: 'md',
          children: [span({ class: 'ui-gallery-card-body' }, [componentName('default')])],
        }),
      ),
      specimen(
        'ui_gallery.state.read',
        uiCard({
          variant: 'sunken',
          padding: 'md',
          children: [span({ class: 'ui-gallery-card-body' }, [componentName('sunken')])],
        }),
      ),
      specimen(
        'ui_gallery.state.active',
        uiCard({
          variant: 'draft',
          padding: 'md',
          children: [span({ class: 'ui-gallery-card-body' }, [componentName('draft')])],
        }),
      ),
    ]),

    // formField — default / help / error / required.
    componentSection('formField', 'ui_gallery.form_field.desc', [
      specimen(
        'ui_gallery.state.default',
        formField({
          id: 'gallery-field-default',
          label: t('ui_gallery.sample.field_label'),
          help: t('ui_gallery.sample.field_help'),
          required: true,
          children: [
            input({
              id: 'gallery-field-default',
              type: 'text',
              class: 'ui-gallery-input',
              'aria-describedby': 'gallery-field-default-help gallery-field-default-error',
            }),
          ],
        }),
      ),
      specimen(
        'ui_gallery.state.disabled',
        formField({
          id: 'gallery-field-error',
          label: t('ui_gallery.sample.field_label'),
          error: t('ui_gallery.sample.field_error'),
          children: [
            input({
              id: 'gallery-field-error',
              type: 'text',
              class: 'ui-gallery-input',
              'aria-invalid': 'true',
              'aria-describedby': 'gallery-field-error-error',
            }),
          ],
        }),
      ),
    ]),

    // selectInput — a representative enum picker.
    componentSection('selectInput', 'ui_gallery.select_input.desc', [
      specimen(
        'ui_gallery.state.default',
        selectInput({
          value: 'canon',
          ariaLabel: t('ui_gallery.sample.select_label'),
          size: 'md',
          options: STATUS_KINDS.map((kind) => ({ value: kind, label: statusLabel(kind) })),
          onChange: () => {
            /* gallery specimen — no model wiring */
          },
        }),
      ),
      specimen(
        'ui_gallery.state.disabled',
        selectInput({
          value: 'pending',
          ariaLabel: t('ui_gallery.sample.select_label'),
          size: 'md',
          disabled: true,
          options: STATUS_KINDS.map((kind) => ({ value: kind, label: statusLabel(kind) })),
          onChange: () => {
            /* gallery specimen — no model wiring */
          },
        }),
      ),
    ]),

    // colorPicker — the §9.3 / §10.2.1 primitive (#219). UNREVIEWED pending
    // design sign-off. Stateless: the gallery passes a static value + inert
    // onChange. Two specimens — a fresh (no selection) state and one with a
    // curated swatch selected (the active swatch shows the ✓ non-colour cue).
    componentSection(
      'colorPicker',
      'ui_gallery.color_picker.desc',
      [
        specimen(
          'ui_gallery.state.default',
          colorPicker({
            value: '',
            swatches: campaignAccentSwatches(),
            messages: colorPickerMessages(),
            testid: 'gallery-color-picker',
            onChange: () => {
              /* gallery specimen — no model wiring */
            },
          }),
        ),
        specimen(
          'ui_gallery.color_picker.specimen.selected',
          colorPicker({
            value: '#3a6f6f',
            swatches: campaignAccentSwatches(),
            messages: colorPickerMessages(),
            onChange: () => {
              /* gallery specimen — no model wiring */
            },
          }),
        ),
      ],
      false,
    ),

    // tabs — a sample tablist (the entity-page pattern). The atom emits
    // `aria-controls="{idBase}-{value}-panel"` on each trigger, so the
    // gallery renders the matching `role="tabpanel"` elements — otherwise
    // `aria-controls` dangles (axe `aria-valid-attr-value`, critical).
    componentSection('tabs', 'ui_gallery.tabs.desc', [
      specimen(
        'ui_gallery.state.default',
        div({ class: 'ui-gallery-tabs-demo' }, [
          tabs({
            idBase: 'gallery-tabs',
            ariaLabel: t('ui_gallery.sample.tabs_label'),
            onActivate: () => undefined,
            items: TAB_SPECIMENS.map((it) => ({ ...it })),
          }),
          ...TAB_SPECIMENS.map((it) =>
            div(
              {
                id: `gallery-tabs-${it.value}-panel`,
                role: 'tabpanel',
                'aria-labelledby': `gallery-tabs-${it.value}-tab`,
                hidden: it.active ? undefined : 'until-found',
                class: 'ui-gallery-tabpanel',
              },
              it.active ? [span({ class: 'ui-gallery-component-name' }, [it.value])] : [],
            ),
          ),
        ]),
      ),
    ]),

    // collapse — a native disclosure.
    componentSection('collapse', 'ui_gallery.collapse.desc', [
      specimen(
        'ui_gallery.state.default',
        collapse({
          openByDefault: true,
          summary: [componentName('collapse')],
          children: [p({ class: 'ui-gallery-collapse-body' }, [text(t('ui_gallery.collapse.desc'))])],
        }),
      ),
    ]),

    // themeToggle — the tri-state cycle button.
    componentSection('themeToggle', 'ui_gallery.theme_toggle.desc', [
      specimen(
        'ui_gallery.state.default',
        themeToggle({
          labels: {
            light: t('theme.light'),
            dark: t('theme.dark'),
            system: t('theme.system'),
          },
        }),
      ),
    ]),

    // ===== §8 atoms — Phase A (UNREVIEWED) =====

    // button — every variant. `icon` carries an aria-label (no visible text).
    componentSection(
      'button',
      'ui_gallery.button.desc',
      BUTTON_VARIANTS.map((variant) =>
        specimen(
          `ui_gallery.state.${variant}`,
          uiButton({
            variant,
            testid: `gallery-button-${variant}`,
            ...(variant === 'icon'
              ? { label: '＋', ariaLabel: t('ui_gallery.sample.page_action') }
              : { label: t('ui_gallery.sample.button_label') }),
            onClick: () => {
              /* gallery specimen — inert */
            },
          }),
        ),
      ),
    ),

    // textInput / textArea — default, invalid, multi-line, and the bare
    // (borderless inline) variant used by the command palette.
    componentSection('textInput', 'ui_gallery.text_input.desc', [
      specimen(
        'ui_gallery.state.single_line',
        textInput({
          value: '',
          placeholder: t('ui_gallery.sample.input_placeholder'),
          ariaLabel: t('ui_gallery.sample.field_label'),
          testid: 'gallery-text-input',
        }),
      ),
      specimen(
        'ui_gallery.state.invalid',
        textInput({
          value: '',
          invalid: true,
          placeholder: t('ui_gallery.sample.input_placeholder'),
          ariaLabel: t('ui_gallery.sample.field_label'),
          testid: 'gallery-text-input-invalid',
        }),
      ),
      specimen(
        'ui_gallery.state.bare',
        // The bare input has no chrome of its own — wrap it in a framed
        // row so the borderless variant reads as a real surface.
        div({ class: 'ui-gallery-bare-row' }, [
          textInput({
            variant: 'bare',
            value: '',
            placeholder: t('ui_gallery.sample.input_placeholder'),
            ariaLabel: t('ui_gallery.sample.field_label'),
            testid: 'gallery-text-input-bare',
          }),
        ]),
      ),
      specimen(
        'ui_gallery.state.multiline',
        textArea({
          value: '',
          rows: 3,
          placeholder: t('ui_gallery.sample.input_placeholder'),
          ariaLabel: t('ui_gallery.sample.field_label'),
          testid: 'gallery-text-area',
        }),
      ),
    ]),

    // badge — generic count/label chip across tones.
    componentSection(
      'badge',
      'ui_gallery.badge.desc',
      BADGE_TONES.map((tone) =>
        specimen(
          `ui_gallery.tone.${tone}`,
          badge({
            tone,
            testid: `gallery-badge-${tone}`,
            children: [text(t(`ui_gallery.tone.${tone}`))],
          }),
        ),
      ),
    ),
  ]);
}

/** A minimal weekless calendar (the mock's "Tidecount") so the gallery can
 * show the grid degrade to a day list. Conforms to `CalendarSchema`. */
const TIDECOUNT_CALENDAR: Calendar = {
  id: 'tidecount',
  label: 'The Tidecount',
  cycles: {
    year: {
      months: [
        { name: 'floodmonth', length: 24 },
        { name: 'ebbmonth', length: 24 },
        { name: 'stillmonth', length: 24 },
      ],
    },
  },
  eras: [
    {
      id: 'ac',
      name: 'After Cataclysm',
      abbreviation: 'AC',
      start_day: null,
      year_one: 1,
      direction: 'forward',
    },
  ],
  leap_rule: { kind: 'none' },
  anchor_canonical_day: 0,
};

/** A canonical day from a display date, or `null` if the fixture is wrong. */
function dayOf(cal: Calendar, era: string, year: number, month: number, day: number): number | null {
  const r = toCanonicalDay(cal, { era, year, month, day });
  return r.ok ? (r.value as number) : null;
}

/**
 * The gallery's own Model slice that backs the INTERACTIVE Gregorian picker
 * specimen (the host that owns the picker's cursor/selection/overlay state).
 * Every other specimen is static (a fixed cursor → a populated grid). The
 * gallery component (`gallery-main.ts`) owns and updates this slice.
 */
export interface GalleryCdpState {
  /** The view cursor (which era/year/month-block is on screen). */
  cdpCursor: CalendarCursor;
  /** Committed canonical day, or null. */
  cdpSelected: number | null;
  /** Which inline overlay is open. */
  cdpOverlay: CalendarDatePickerOverlay;
  /** Whether the interactive `dateField` specimen's popover grid is open.
   * The field reuses the cdp* cursor/selection/overlay slices above. */
  dfOpen: boolean;
  /** The LIVE calendarEditor working draft — the host owns it so editing a
   * month length re-renders the embedded preview picker instantly. */
  ceWorking: WorkingCalendar;
  /** Which editor surface is shown: simple-rename vs the advanced full model. */
  ceMode: 'simple' | 'advanced';
  /** The LIVE trait-editor working draft — the host owns it so adding/renaming
   * a field or switching a value-kind re-renders the form + build preview
   * instantly (the same host-owned pattern as `ceWorking`). */
  teState: TraitEditorState;

  // --- LIVE fact-row editor slice ---------------------------------------
  // `GalleryCdpState` structurally satisfies `FactRowStateShape` (below) so the
  // real, generic `viewEntityFactRows` can be driven directly from the gallery
  // with a host harness (the same host-owned pattern as the trait/calendar
  // editors). A gallery-injected homebrew trait (see `galleryHomebrewDefs`)
  // surfaces one predicate per value-kind on the mock entity.
  entities: Record<string, EntityState>;
  factDirty: Record<string, true>;
  factSavedAt: Record<string, number>;
  factSaving: Record<string, true>;
  factDirtyEpoch: Record<string, number>;
  inlineErrors: Record<string, string>;
  focusedFactKey: string | null;
  factHintDismissed: boolean;
  activeCalendarId: string;
  inputBuffer: Record<string, string>;
  aiDraftPending: Record<string, true>;
  aiDraftError: Record<string, string>;
  assetUrls: Record<string, string>;
  historyOpen: { entityId: string; predicate: string } | null;
  highlightedTrait: string | null;
  /** The single open date-picker grid (host-owned), or null. */
  frDatePicker: { id: string; cursor: CalendarCursor; overlay: CalendarDatePickerOverlay } | null;
  /** Mock session cursor seeding a fresh validity bound / the date field. */
  frCampaignCursor: number | null;
  /** The standalone validityEditor (write) specimen's working value, so its
   * folded-in date picker + precision toggle are fully editable in the gallery
   * (reuses the `frDatePicker` open slice above). */
  valValidity: FactValidity | null;
}

/** The messages the gallery's `update` handles for the interactive picker
 * AND the live calendarEditor. */
export type GalleryCdpMsg =
  | { type: 'cdp-navigate'; cursor: CalendarCursor }
  | { type: 'cdp-select'; day: number }
  | { type: 'cdp-clear' }
  | { type: 'cdp-overlay'; overlay: CalendarDatePickerOverlay }
  // --- interactive dateField specimen (reuses the cdp* slices) ---
  | { type: 'df-open'; cursor: CalendarCursor }
  | { type: 'df-close' }
  // --- calendarEditor edits (host owns the working draft) ---
  | { type: 'ce-name'; name: string }
  | { type: 'ce-month'; index: number; patch: Partial<WorkingMonth> }
  | { type: 'ce-add-month' }
  | { type: 'ce-remove-month'; index: number }
  | { type: 'ce-toggle-intercalary'; index: number }
  | { type: 'ce-leap-rule'; rule: LeapRule }
  | { type: 'ce-era'; index: number; patch: Partial<WorkingEra> }
  | { type: 'ce-add-era' }
  | { type: 'ce-remove-era'; index: number }
  | { type: 'ce-weekday'; index: number; name: string }
  | { type: 'ce-add-weekday' }
  | { type: 'ce-remove-weekday'; index: number }
  | { type: 'ce-phase'; phase: string }
  | { type: 'ce-period'; index: number; name: string }
  | { type: 'ce-add-period' }
  | { type: 'ce-remove-period'; index: number }
  | { type: 'ce-anchor-day'; day: string }
  | { type: 'ce-anchor-name'; name: string }
  | { type: 'ce-toggle-advanced' }
  // --- live trait editor (host owns the working draft) ---
  | { type: 'te-name'; name: string }
  | { type: 'te-add-field' }
  | { type: 'te-set-field'; index: number; patch: Partial<TraitEditorField> }
  | { type: 'te-remove-field'; index: number }
  | { type: 'te-add-tuple'; field: number }
  | { type: 'te-set-tuple'; field: number; sub: number; patch: Partial<TupleEditorField> }
  | { type: 'te-remove-tuple'; field: number; sub: number }
  // --- live fact-row editor (host drives the real viewEntityFactRows) ---
  | { type: 'fr-edit'; entityId: string; predicate: string; value: unknown }
  | { type: 'fr-soft-delete'; entityId: string; predicate: string }
  | { type: 'fr-mark-dirty'; entityId: string; predicate: string; buffer?: string }
  | { type: 'fr-focus'; entityId: string; predicate: string }
  | { type: 'fr-blur'; entityId: string; predicate: string }
  | { type: 'fr-set-validity'; entityId: string; predicate: string; validity: FactValidity | null }
  | { type: 'val-set'; validity: FactValidity | null }
  | { type: 'fr-open-datepicker'; id: string; cursor: CalendarCursor }
  | { type: 'fr-navigate-datepicker'; cursor: CalendarCursor }
  | { type: 'fr-overlay-datepicker'; overlay: CalendarDatePickerOverlay }
  | { type: 'fr-close-datepicker' }
  /** No-op channel for the showcase-irrelevant host actions (AI draft, image
   * gen, reveal menu, rollback, succeed, ratify/reject, invalid). */
  | { type: 'fr-noop' };

/** A worldbuilt starting draft for the LIVE calendarEditor specimen — a
 * Gregorian fork renamed into "Calendar of the Reaches" (the mock's running
 * example), with one intercalary festival so the advanced model is exercised. */
function initialWorkingCalendar(): WorkingCalendar {
  return {
    id: 'cal_11111111-1111-4111-8111-111111111111',
    name: t('ui_gallery.calendar_editor.sample.reaches_name'),
    months: [
      { name: 'Deepwinter', length: '31' },
      { name: 'Thawing', length: '28', leapLength: '29' },
      { name: 'Sowing', length: '31' },
      { name: 'Reaping', length: '30' },
      { name: 'Highsun', length: '31' },
      { name: 'Midsummer', length: '1', intercalary: true },
      { name: 'Harvest', length: '30' },
    ],
    leapRule: { kind: 'gregorian' },
    eras: [
      {
        id: 'ir',
        name: 'Imperial Reckoning',
        abbreviation: 'IR',
        startDay: '',
        yearOne: '1067',
        direction: 'forward',
      },
    ],
    weekdays: {
      labels: [
        { name: 'Sul' },
        { name: 'Mol' },
        { name: 'Zor' },
        { name: 'Wel' },
        { name: 'Thar' },
        { name: 'Far' },
        { name: 'Sar' },
      ],
      phase: '0',
    },
    subDayPeriods: [{ name: 'Deepnight' }, { name: 'Dawn' }, { name: 'Highsun' }, { name: 'Dusk' }],
    anchorCanonicalDay: '0',
    anchorName: 'The Founding',
  };
}

/** A fresh, empty trait-editor field (mirrors app.ts `emptyTraitEditorField`). */
function emptyTeField(key: number): TraitEditorField {
  return { key, id: '', valueKind: 'text', cardinality: 'single', enumValues: '', refRoot: '' };
}

/** A worldbuilt starting draft for the LIVE trait editor: an "office" trait with
 * a number field, an enum field, and a structured-record (tuple) field — so the
 * specimen exercises the value-kind picker, choices, and tuple sub-field
 * authoring out of the box. */
function initialTraitEditorState(): TraitEditorState {
  return {
    mode: 'create',
    forRoot: 'faction',
    appliesTo: ['faction'],
    traitId: 'governed',
    fields: [
      { key: 0, id: 'population', valueKind: 'number', cardinality: 'single' },
      { key: 1, id: 'government', valueKind: 'enum', cardinality: 'single', enumValues: 'Monarchy, Council, Theocracy' },
      {
        key: 2,
        id: 'office',
        valueKind: 'tuple',
        cardinality: 'many',
        tupleFields: [
          { key: 0, name: 'role', valueKind: 'enum', optional: false, enumValues: 'Steward, Warden' },
          { key: 1, name: 'holder', valueKind: 'ref', optional: false, refRoot: 'character' },
          { key: 2, name: 'note', valueKind: 'text', optional: true },
        ],
      },
    ],
    nextFieldKey: 3,
    allowRisky: false,
    error: null,
  };
}

// ── LIVE fact-row editor harness ───────────────────────────────────────
// A gallery-injected homebrew trait surfaces ONE predicate per value-kind on a
// mock entity, so the real, generic `viewEntityFactRows` renders the full
// spread live. `setHomebrewDefs(galleryHomebrewDefs())` is called from
// `gallery-main.ts` (the standalone entry) ONLY — never at this module's scope,
// because the in-app `/ui-gallery` route imports this module and must not get
// gallery vocab in its real campaign registry.

const GX_TRAIT = 'trait_9a110000-0000-4000-8000-000000000001';
const GX_KEEP = 'gx_keep';
const GX_ARIA = 'gx_aria';
const GX_BRYN = 'gx_bryn';
/** Predicate ids, one per value-kind shown live. */
const GX = {
  text: 'pred_9a110000-0000-4000-8000-000000000001',
  number: 'pred_9a110000-0000-4000-8000-000000000002',
  boolean: 'pred_9a110000-0000-4000-8000-000000000003',
  enum: 'pred_9a110000-0000-4000-8000-000000000004',
  ref: 'pred_9a110000-0000-4000-8000-000000000005',
  rich: 'pred_9a110000-0000-4000-8000-000000000006',
  date: 'pred_9a110000-0000-4000-8000-000000000007',
  tuple: 'pred_9a110000-0000-4000-8000-000000000008',
} as const;

function gxPred(
  id: string,
  name: string,
  valueKind: ValueKind,
  extra: Partial<PredicateDeclaration> = {},
): PredicateDeclaration {
  return {
    id,
    label: { en: name },
    value_kind: [valueKind],
    merge: 'lww',
    previous_names: [],
    ...extra,
  } as PredicateDeclaration;
}

/**
 * The gallery's injected homebrew overlay: one predicate per value-kind bundled
 * into a single trait so the mock entity surfaces them all. Standalone-only
 * (injected in `gallery-main.ts`).
 */
export function galleryHomebrewDefs() {
  const predicates: PredicateDeclaration[] = [
    gxPred(GX.text, 'Motto', 'text'),
    gxPred(GX.number, 'Population', 'number'),
    gxPred(GX.boolean, 'Walled', 'boolean', {
      boolean_labels: { true_label: 'Walled', false_label: 'Open' },
    }),
    gxPred(GX.enum, 'Government', 'enum', {
      enum_values: [
        { id: 'enm_9a11_monarchy', label: { en: 'Monarchy' } },
        { id: 'enm_9a11_council', label: { en: 'Council' } },
        { id: 'enm_9a11_theocracy', label: { en: 'Theocracy' } },
      ],
    }),
    gxPred(GX.ref, 'Ruled by', 'ref', { ref_roots: ['character'] }),
    gxPred(GX.rich, 'History', 'rich_text'),
    gxPred(GX.date, 'Founded on', 'game_date'),
    gxPred(GX.tuple, 'Office', 'tuple', {
      tuple_fields: [
        {
          id: 'fld_9a11_role',
          label: { en: 'role' },
          value_kind: 'enum',
          enum_values: [
            { id: 'enm_9a11_steward', label: { en: 'Steward' } },
            { id: 'enm_9a11_warden', label: { en: 'Warden' } },
          ],
        },
        { id: 'fld_9a11_holder', label: { en: 'holder' }, value_kind: 'ref', ref_roots: ['character'] },
        { id: 'fld_9a11_note', label: { en: 'note' }, value_kind: 'text', optional: true },
      ],
    }),
  ];
  const trait: TraitDeclaration = {
    id: GX_TRAIT,
    label: { en: 'Showcase fields' },
    applies_to: ['location'],
    predicates: [GX.text, GX.number, GX.boolean, GX.enum, GX.ref, GX.rich, GX.date, GX.tuple],
    safe_to_bulk_reveal: false,
  } as TraitDeclaration;
  return {
    predicates,
    traits: [trait],
    types: [],
    dimensions: [],
    units: [],
    calendars: [],
    retiredDimensions: {},
    retiredUnits: {},
    retiredCalendars: {},
    aliases: {},
    aliasAdvisories: [],
  };
}

/** One fact chain at a single GM-authored version. */
function gxFact(value: unknown): FactState {
  return {
    versions: [{ value, written_at_real: '2026-01-01T00:00:00.000Z', author: 'GM' }],
    soft_deleted: false,
    status: DEFAULT_FACT_STATUS,
    owner: DEFAULT_FACT_OWNER,
    validity: null,
    reveals: [],
  };
}

/** The mock entities backing the live fact-row editor: a keep carrying one fact
 * per value-kind, plus two characters its `ref` / tuple-holder fields resolve. */
function galleryFactRowEntities(): Record<string, EntityState> {
  const greg = loadBundledCalendar(GREGORIAN_CALENDAR_ID);
  const founded = dayOf(greg, 'ce', 1247, 5, 12) ?? greg.anchor_canonical_day;
  const character = (id: string, name: string): EntityState => ({
    id,
    root: 'character',
    traits: [],
    facts: { name: gxFact(name) },
    soft_deleted: false,
  });
  return {
    [GX_ARIA]: character(GX_ARIA, 'Queen Aria'),
    [GX_BRYN]: character(GX_BRYN, 'Ser Bryn'),
    [GX_KEEP]: {
      id: GX_KEEP,
      root: 'location',
      traits: [GX_TRAIT],
      facts: {
        name: gxFact('Stormhold Keep'),
        [GX.text]: gxFact('Hold the line.'),
        [GX.number]: gxFact(12000),
        [GX.boolean]: gxFact(true),
        [GX.enum]: gxFact('enm_9a11_monarchy'),
        [GX.ref]: gxFact(GX_ARIA),
        [GX.rich]: gxFact('A **towering** basalt fortress with *silver* gates.'),
        [GX.date]: gxFact(founded),
        [GX.tuple]: gxFact({
          fld_9a11_role: 'enm_9a11_steward',
          fld_9a11_holder: GX_ARIA,
          fld_9a11_note: 'Appointed at the founding.',
        }),
      },
      soft_deleted: false,
    },
  };
}

/** Localized message catalog for the embedded date-picker grid (mirrors the
 * app's `datePickerMessages`). */
function galleryDatePickerMessages(): Partial<CalendarDatePickerMessages & DateFieldMessages> {
  return {
    prev: t('calendar.grid.prev'),
    next: t('calendar.grid.next'),
    month: t('calendar.month'),
    yearEra: t('calendar.grid.year_era'),
    grid: t('calendar.grid.days'),
    now: t('calendar.grid.now'),
    selectYear: t('calendar.grid.select_year'),
    clear: t('calendar.clear'),
    set: t('calendar.grid.set'),
    unset: t('calendar.grid.no_date'),
    granularity: t('calendar.grid.granularity'),
    granularityOptions: {
      day: t('calendar.grid.granularity.day'),
      month: t('calendar.grid.granularity.month'),
      season: t('calendar.grid.granularity.season'),
      year: t('calendar.grid.granularity.year'),
    },
    placeholder: t('calendar.field.placeholder'),
    trigger: t('calendar.field.trigger'),
    close: t('calendar.field.close'),
  };
}

/** The fact-row editor host, bound to the gallery's reducer. Mutating actions
 * (edit / delete / dirty / validity / date-picker) drive real `fr-*` Msgs;
 * showcase-irrelevant actions (AI, reveal, rollback, …) are no-ops. */
function galleryFactRowHost(): FactRowHost<GalleryCdpState, GalleryCdpMsg> {
  const noop = (): GalleryCdpMsg => ({ type: 'fr-noop' });
  return {
    activeCalendar: (s) => loadBundledCalendar(s.activeCalendarId),
    campaignCursor: (s) => s.frCampaignCursor,
    browseCursor: () => null,
    datePicker: (s) => s.frDatePicker,
    datePickerMessages: () => galleryDatePickerMessages(),
    openDatePicker: (id, cursor) => ({ type: 'fr-open-datepicker', id, cursor }),
    navigateDatePicker: (cursor) => ({ type: 'fr-navigate-datepicker', cursor }),
    overlayDatePicker: (overlay) => ({ type: 'fr-overlay-datepicker', overlay }),
    closeDatePicker: () => ({ type: 'fr-close-datepicker' }),
    editFact: (entityId, predicate, value) => ({ type: 'fr-edit', entityId, predicate, value }),
    softDeleteFact: (entityId, predicate) => ({ type: 'fr-soft-delete', entityId, predicate }),
    invalidFact: () => noop(),
    rollbackFact: () => noop(),
  openHistory: () => noop(),
  closeHistory: () => noop(),
    setFactValidity: (entityId, predicate, validity) => ({
      type: 'fr-set-validity',
      entityId,
      predicate,
      validity,
    }),
    succeedFact: () => noop(),
    markDirty: (entityId, predicate, buffer) => ({
      type: 'fr-mark-dirty',
      entityId,
      predicate,
      ...(buffer !== undefined ? { buffer } : {}),
    }),
    focusFact: (entityId, predicate) => ({ type: 'fr-focus', entityId, predicate }),
    blurFact: (entityId, predicate) => ({ type: 'fr-blur', entityId, predicate }),
    dismissHint: () => noop(),
    draftWithAi: () => noop(),
    generateImageFor: () => noop(),
    ratifyDraft: () => noop(),
    rejectDraft: () => noop(),
    clearReviewState: () => noop(),
    openRevealMenu: () => noop(),
    uploadAsset: () => Promise.resolve({ id: 'asset_gallery', url: '' }),
  };
}

/** Apply a fact mutation to one entity's chain (immutable). */
function frPatchFact(
  state: GalleryCdpState,
  entityId: string,
  predicate: string,
  patch: (fact: FactState | undefined) => FactState | undefined,
): Record<string, EntityState> {
  const entity = state.entities[entityId];
  if (!entity) return state.entities;
  const next = patch(entity.facts[predicate]);
  const facts = { ...entity.facts };
  if (next === undefined) delete facts[predicate];
  else facts[predicate] = next;
  return { ...state.entities, [entityId]: { ...entity, facts } };
}

/** Initial state for the interactive Gregorian specimen + the live editors. */
export function initialGalleryCdpState(): GalleryCdpState {
  return {
    cdpCursor: { eraId: 'ce', year: 2026, monthIndex: 5 },
    cdpSelected: dayOf(loadBundledCalendar(GREGORIAN_CALENDAR_ID), 'ce', 2026, 5, 22),
    cdpOverlay: 'none',
    dfOpen: false,
    ceWorking: initialWorkingCalendar(),
    ceMode: 'advanced',
    teState: initialTraitEditorState(),
    // Live fact-row editor slice.
    entities: galleryFactRowEntities(),
    factDirty: {},
    factSavedAt: {},
    factSaving: {},
    factDirtyEpoch: {},
    inlineErrors: {},
    focusedFactKey: null,
    factHintDismissed: true,
    activeCalendarId: GREGORIAN_CALENDAR_ID,
    inputBuffer: {},
    aiDraftPending: {},
    aiDraftError: {},
    assetUrls: {},
    historyOpen: null,
    highlightedTrait: null,

    frDatePicker: null,
    frCampaignCursor: dayOf(loadBundledCalendar(GREGORIAN_CALENDAR_ID), 'ce', 2026, 6, 17),
    // Seed the write-specimen with a bounded-from / open-to value at month
    // precision, so the folded-in picker + precision toggle have a date to show.
    valValidity: {
      from: loadBundledCalendar(GREGORIAN_CALENDAR_ID).anchor_canonical_day,
      from_precision: 'month',
    },
  };
}

/** Patch one month in the working draft. */
function patchMonth(months: WorkingMonth[], index: number, patch: Partial<WorkingMonth>): WorkingMonth[] {
  return months.map((mo, i) => (i === index ? { ...mo, ...patch } : mo));
}

/** Patch one era in the working draft. */
function patchEra(eras: WorkingEra[], index: number, patch: Partial<WorkingEra>): WorkingEra[] {
  return eras.map((e, i) => (i === index ? { ...e, ...patch } : e));
}

/** Reducer for the interactive Gregorian picker AND the live editor. */
export function reduceGalleryCdp(state: GalleryCdpState, msg: GalleryCdpMsg): GalleryCdpState {
  switch (msg.type) {
    case 'cdp-navigate':
      return { ...state, cdpCursor: msg.cursor, cdpOverlay: 'none' };
    case 'cdp-select':
      return { ...state, cdpSelected: msg.day };
    case 'cdp-clear':
      return { ...state, cdpSelected: null };
    case 'cdp-overlay':
      return { ...state, cdpOverlay: msg.overlay };
    case 'df-open':
      return { ...state, dfOpen: true, cdpCursor: msg.cursor, cdpOverlay: 'none' };
    case 'df-close':
      return { ...state, dfOpen: false };
    // --- calendarEditor edits ---
    case 'ce-name':
      return { ...state, ceWorking: { ...state.ceWorking, name: msg.name } };
    case 'ce-month':
      return {
        ...state,
        ceWorking: { ...state.ceWorking, months: patchMonth(state.ceWorking.months, msg.index, msg.patch) },
      };
    case 'ce-add-month':
      return {
        ...state,
        ceWorking: {
          ...state.ceWorking,
          months: [...state.ceWorking.months, { name: 'New month', length: '30' }],
        },
      };
    case 'ce-remove-month':
      return {
        ...state,
        ceWorking: {
          ...state.ceWorking,
          months: state.ceWorking.months.filter((_, i) => i !== msg.index),
        },
      };
    case 'ce-toggle-intercalary':
      return {
        ...state,
        ceWorking: {
          ...state.ceWorking,
          months: state.ceWorking.months.map((mo, i) =>
            i === msg.index ? { ...mo, intercalary: !(mo.intercalary === true) } : mo,
          ),
        },
      };
    case 'ce-leap-rule':
      return { ...state, ceWorking: { ...state.ceWorking, leapRule: msg.rule } };
    case 'ce-era':
      return {
        ...state,
        ceWorking: { ...state.ceWorking, eras: patchEra(state.ceWorking.eras, msg.index, msg.patch) },
      };
    case 'ce-add-era':
      return {
        ...state,
        ceWorking: {
          ...state.ceWorking,
          eras: [
            ...state.ceWorking.eras,
            {
              id: `era_${state.ceWorking.eras.length}`,
              name: 'New era',
              startDay: '0',
              yearOne: '1',
              direction: 'forward',
            },
          ],
        },
      };
    case 'ce-remove-era':
      return {
        ...state,
        ceWorking: { ...state.ceWorking, eras: state.ceWorking.eras.filter((_, i) => i !== msg.index) },
      };
    case 'ce-weekday': {
      const wd = state.ceWorking.weekdays;
      if (wd === undefined) return state;
      return {
        ...state,
        ceWorking: {
          ...state.ceWorking,
          weekdays: {
            ...wd,
            labels: wd.labels.map((w, i) => (i === msg.index ? { name: msg.name } : w)),
          },
        },
      };
    }
    case 'ce-add-weekday':
      return {
        ...state,
        ceWorking: {
          ...state.ceWorking,
          weekdays:
            state.ceWorking.weekdays === undefined
              ? { labels: [{ name: 'Day' }], phase: '0' }
              : {
                  ...state.ceWorking.weekdays,
                  labels: [...state.ceWorking.weekdays.labels, { name: 'Day' }],
                },
        },
      };
    case 'ce-remove-weekday': {
      const wd = state.ceWorking.weekdays;
      if (wd === undefined) return state;
      return {
        ...state,
        ceWorking: {
          ...state.ceWorking,
          weekdays: { ...wd, labels: wd.labels.filter((_, i) => i !== msg.index) },
        },
      };
    }
    case 'ce-phase': {
      const wd = state.ceWorking.weekdays;
      if (wd === undefined) return state;
      return {
        ...state,
        ceWorking: { ...state.ceWorking, weekdays: { ...wd, phase: msg.phase } },
      };
    }
    case 'ce-period':
      return {
        ...state,
        ceWorking: {
          ...state.ceWorking,
          subDayPeriods: (state.ceWorking.subDayPeriods ?? []).map((p, i) =>
            i === msg.index ? { name: msg.name } : p,
          ),
        },
      };
    case 'ce-add-period':
      return {
        ...state,
        ceWorking: {
          ...state.ceWorking,
          subDayPeriods: [...(state.ceWorking.subDayPeriods ?? []), { name: 'New period' }],
        },
      };
    case 'ce-remove-period':
      return {
        ...state,
        ceWorking: {
          ...state.ceWorking,
          subDayPeriods: (state.ceWorking.subDayPeriods ?? []).filter((_, i) => i !== msg.index),
        },
      };
    case 'ce-anchor-day':
      return { ...state, ceWorking: { ...state.ceWorking, anchorCanonicalDay: msg.day } };
    case 'ce-anchor-name':
      return { ...state, ceWorking: { ...state.ceWorking, anchorName: msg.name } };
    case 'ce-toggle-advanced':
      return { ...state, ceMode: state.ceMode === 'advanced' ? 'simple' : 'advanced' };
    // --- live trait editor (mirrors app.ts's trait-editor reducer arms) ---
    case 'te-name':
      return { ...state, teState: { ...state.teState, traitId: msg.name, error: null } };
    case 'te-add-field':
      return {
        ...state,
        teState: {
          ...state.teState,
          fields: [...state.teState.fields, emptyTeField(state.teState.nextFieldKey)],
          nextFieldKey: state.teState.nextFieldKey + 1,
          error: null,
        },
      };
    case 'te-set-field':
      return {
        ...state,
        teState: {
          ...state.teState,
          fields: state.teState.fields.map((f, i) => (i === msg.index ? { ...f, ...msg.patch } : f)),
          error: null,
        },
      };
    case 'te-remove-field':
      return {
        ...state,
        teState: {
          ...state.teState,
          fields: state.teState.fields.filter((_, i) => i !== msg.index),
          error: null,
        },
      };
    case 'te-add-tuple':
      return {
        ...state,
        teState: {
          ...state.teState,
          fields: state.teState.fields.map((f, i) => {
            if (i !== msg.field) return f;
            const subs = f.tupleFields ?? [];
            const nextKey = subs.reduce((m, s) => Math.max(m, s.key + 1), 0);
            return {
              ...f,
              tupleFields: [...subs, { key: nextKey, name: '', valueKind: 'text' as TupleFieldKind, optional: false }],
            };
          }),
          error: null,
        },
      };
    case 'te-set-tuple':
      return {
        ...state,
        teState: {
          ...state.teState,
          fields: state.teState.fields.map((f, i) =>
            i === msg.field
              ? { ...f, tupleFields: (f.tupleFields ?? []).map((s, j) => (j === msg.sub ? { ...s, ...msg.patch } : s)) }
              : f,
          ),
          error: null,
        },
      };
    case 'te-remove-tuple':
      return {
        ...state,
        teState: {
          ...state.teState,
          fields: state.teState.fields.map((f, i) =>
            i === msg.field ? { ...f, tupleFields: (f.tupleFields ?? []).filter((_, j) => j !== msg.sub) } : f,
          ),
          error: null,
        },
      };
    // --- live fact-row editor ---
    case 'fr-edit': {
      const key = factRowKey(msg.entityId, msg.predicate);
      return {
        ...state,
        entities: frPatchFact(state, msg.entityId, msg.predicate, () =>
          gxFact(msg.value),
        ),
        factDirty: omitKey(state.factDirty, key),
        inputBuffer: omitKey(state.inputBuffer, key),
        factSavedAt: { ...state.factSavedAt, [key]: 1 },
      };
    }
    case 'fr-soft-delete': {
      const key = factRowKey(msg.entityId, msg.predicate);
      return {
        ...state,
        entities: frPatchFact(state, msg.entityId, msg.predicate, () => undefined),
        factDirty: omitKey(state.factDirty, key),
        inputBuffer: omitKey(state.inputBuffer, key),
      };
    }
    case 'fr-mark-dirty': {
      const key = factRowKey(msg.entityId, msg.predicate);
      return {
        ...state,
        factDirty: { ...state.factDirty, [key]: true },
        factDirtyEpoch: { ...state.factDirtyEpoch, [key]: (state.factDirtyEpoch[key] ?? 0) + 1 },
        ...(msg.buffer !== undefined
          ? { inputBuffer: { ...state.inputBuffer, [key]: msg.buffer } }
          : {}),
      };
    }
    case 'fr-focus':
      return { ...state, focusedFactKey: factRowKey(msg.entityId, msg.predicate) };
    case 'fr-blur':
      return { ...state, focusedFactKey: null };
    case 'fr-set-validity':
      return {
        ...state,
        entities: frPatchFact(state, msg.entityId, msg.predicate, (fact) =>
          fact ? { ...fact, validity: msg.validity } : fact,
        ),
      };
    case 'fr-open-datepicker':
      return { ...state, frDatePicker: { id: msg.id, cursor: msg.cursor, overlay: 'none' } };
    case 'fr-navigate-datepicker':
      // Update only the cursor, LEAVE the overlay (matches app.ts
      // `date-picker-navigate`). The picker closes the overlay itself via an
      // explicit `onOverlay('none')` where needed (block step / month pick) —
      // so the era stepper's +/− keep it open for repeated clicks.
      return state.frDatePicker
        ? { ...state, frDatePicker: { ...state.frDatePicker, cursor: msg.cursor } }
        : state;
    case 'fr-overlay-datepicker':
      return state.frDatePicker
        ? { ...state, frDatePicker: { ...state.frDatePicker, overlay: msg.overlay } }
        : state;
    case 'fr-close-datepicker':
      return { ...state, frDatePicker: null };
    case 'val-set':
      return { ...state, valValidity: msg.validity };
    case 'fr-noop':
      return state;
  }
}

/** Immutable delete of one key from a Record. */
function omitKey<V>(rec: Record<string, V>, key: string): Record<string, V> {
  if (!(key in rec)) return rec;
  const next = { ...rec };
  delete next[key];
  return next;
}

/**
 * A host that lets the Gregorian specimen be INTERACTIVE. The gallery
 * (`gallery-main.ts`) owns the cursor/selection/overlay in its Model and
 * passes this binding; when it is absent (the embedded `/ui-gallery` route in
 * `app.ts`, whose Model has no CDP slice) the specimen falls back to a fixed
 * static cursor that still renders a populated grid.
 */
export interface GalleryCdpHost {
  state: Signal<GalleryCdpState>;
  send: Send<GalleryCdpMsg>;
}

/**
 * The INTERACTIVE Gregorian specimen. The host owns the
 * cursor/selection/overlay; the picker is a stateless atom that emits intent
 * through callbacks. To remount the picker on any of those changes (the
 * construction-time atom pattern), it is wrapped in a keyed `each` whose key
 * is a digest of the host slice — so navigate/select/open rebuilds the grid
 * declaratively.
 */
function interactiveGregorian(host: GalleryCdpHost): Mountable {
  const gregorian = loadBundledCalendar(GREGORIAN_CALENDAR_ID);
  const today = dayOf(gregorian, 'ce', 2026, 6, 17);
  const { state, send } = host;
  const keyOf = (s: GalleryCdpState): string =>
    `${s.cdpCursor.eraId}|${s.cdpCursor.year}|${s.cdpCursor.monthIndex}|${s.cdpSelected ?? 'x'}|${s.cdpOverlay}`;
  const model = derived([state], (s) => s);
  return each(
    model.map((s) => [keyOf(s)]),
    {
      key: (k) => k,
      render: () => {
        const s = model.peek();
        return [
          calendarDatePicker({
            calendar: gregorian,
            cursor: s.cdpCursor,
            value: s.cdpSelected,
            overlay: s.cdpOverlay,
            today,
            locale: 'en',
            testid: 'gallery-cdp-gregorian',
            onNavigate: (cursor) => send({ type: 'cdp-navigate', cursor }),
            onSelect: (day) => send({ type: 'cdp-select', day: day as number }),
            onClear: () => send({ type: 'cdp-clear' }),
            onOverlay: (overlay) => send({ type: 'cdp-overlay', overlay }),
          }),
        ];
      },
    },
  );
}

/**
 * The INTERACTIVE `dateField` specimen — the compact field shell that opens
 * the grid in a popover. It reuses the host's cdp* cursor/selection/overlay
 * slices (so picking here and in the inline grid stay in sync) plus a `dfOpen`
 * flag. Like the inline picker it is a stateless atom wrapped in a keyed
 * `each` digesting the open view-state so it remounts on navigation.
 */
function interactiveDateField(host: GalleryCdpHost): Mountable {
  const gregorian = loadBundledCalendar(GREGORIAN_CALENDAR_ID);
  const today = dayOf(gregorian, 'ce', 2026, 6, 17);
  const { state, send } = host;
  const keyOf = (s: GalleryCdpState): string =>
    `${s.dfOpen ? `${s.cdpCursor.eraId}|${s.cdpCursor.year}|${s.cdpCursor.monthIndex}|${s.cdpOverlay}` : 'closed'}|${s.cdpSelected ?? 'x'}`;
  const model = derived([state], (s) => s);
  return each(
    model.map((s) => [keyOf(s)]),
    {
      key: (k) => k,
      render: () => {
        const s = model.peek();
        return [
          dateField({
            calendar: gregorian,
            value: s.cdpSelected,
            open: s.dfOpen,
            ...(s.dfOpen ? { cursor: s.cdpCursor, overlay: s.cdpOverlay } : {}),
            today,
            clearable: true,
            id: 'gallery-date-field',
            testid: 'gallery-date-field',
            locale: 'en',
            onOpen: (cursor) => send({ type: 'df-open', cursor }),
            onClose: () => send({ type: 'df-close' }),
            onNavigate: (cursor) => send({ type: 'cdp-navigate', cursor }),
            onOverlay: (overlay) => send({ type: 'cdp-overlay', overlay }),
            onSelect: (day) => send({ type: 'cdp-select', day: day as number }),
            onClear: () => send({ type: 'cdp-clear' }),
          }),
        ];
      },
    },
  );
}

/**
 * The INTERACTIVE `validityEditor` (write) specimen — fully editable, with the
 * date precision folded INTO each bound's picker (the real app wiring). The
 * host owns the working value (`valValidity`) + the single open date-picker
 * slice (`frDatePicker`, shared with the fact-row specimen). The whole editor
 * is wrapped in a keyed `each` digesting both, so committing a bound, opening
 * its grid, navigating, or switching granularity remounts it declaratively
 * (the same outer-remount pattern the app's fact row relies on).
 */
function liveValidityEditor(host: GalleryCdpHost, id: string, testid: string): Mountable {
  const calendar = loadBundledCalendar(GREGORIAN_CALENDAR_ID);
  const { state, send } = host;
  const keyOf = (s: GalleryCdpState): string => {
    const v = s.valValidity;
    const dp = s.frDatePicker;
    const dpKey = dp
      ? `${dp.id}|${dp.cursor.eraId}|${dp.cursor.year}|${dp.cursor.monthIndex}|${dp.overlay}`
      : 'dp0';
    return v
      ? `${v.from ?? 'x'}:${v.to ?? 'x'}:${v.from_precision ?? '-'}:${v.to_precision ?? '-'}:${dpKey}`
      : `none:${dpKey}`;
  };
  const model = derived([state], (s) => s);
  return each(model.map((s) => [keyOf(s)]), {
    key: (k) => k,
    render: () => {
      const s = model.peek();
      return [
        validityEditor({
          calendar,
          value: s.valValidity,
          id,
          testid,
          mode: 'edit',
          ...(s.frCampaignCursor !== null ? { defaultSeed: s.frCampaignCursor } : {}),
          messages: galleryDatePickerMessages(),
          // Inject the real calendar-grid dateField for each bound — the
          // precision toggle is folded into its popover (no external <select>).
          // The single `frDatePicker` slice drives open/cursor/overlay; the
          // outer `each` above remounts on every change, so a plain `dateField`
          // from the snapshot suffices (no nested per-bound `each`).
          renderPicker: ({
            value,
            onChange,
            id,
            testid,
            placeholder,
            granularity,
            granularities,
            onGranularity,
            granularityClearLabel,
            onGranularityClear,
          }) => {
            const dp = s.frDatePicker;
            const open = dp !== null && dp.id === id;
            return dateField({
              calendar,
              value,
              open,
              ...(open ? { cursor: dp.cursor, overlay: dp.overlay } : {}),
              clearable: false,
              id,
              testid: testid ?? id,
              locale: 'en',
              messages: { ...galleryDatePickerMessages(), placeholder },
              granularity,
              granularities,
              onGranularity,
              granularityClearLabel,
              onGranularityClear,
              onOpen: (cursor) => send({ type: 'fr-open-datepicker', id, cursor }),
              onClose: () => send({ type: 'fr-close-datepicker' }),
              onNavigate: (cursor) => send({ type: 'fr-navigate-datepicker', cursor }),
              onOverlay: (overlay) => send({ type: 'fr-overlay-datepicker', overlay }),
              onSelect: (day) => onChange(day as number),
            });
          },
          onChange: (next) => send({ type: 'val-set', validity: next }),
        }),
      ];
    },
  });
}

/** The static `dateField` specimen (closed, fixed value) for the embedded
 * `/ui-gallery` route whose Model has no cdp slice. */
function staticDateField(): Mountable {
  const gregorian = loadBundledCalendar(GREGORIAN_CALENDAR_ID);
  return dateField({
    calendar: gregorian,
    value: dayOf(gregorian, 'ce', 2026, 5, 22),
    open: false,
    id: 'gallery-date-field',
    testid: 'gallery-date-field',
    locale: 'en',
    onOpen: () => undefined,
    onClose: () => undefined,
    onNavigate: () => undefined,
    onOverlay: () => undefined,
    onSelect: () => undefined,
  });
}

/** The static Gregorian specimen (fixed cursor + selection) used when no
 * interactive host is wired (the embedded `/ui-gallery` route). */
function staticGregorian(): Mountable {
  const gregorian = loadBundledCalendar(GREGORIAN_CALENDAR_ID);
  return calendarDatePicker({
    calendar: gregorian,
    cursor: { eraId: 'ce', year: 2026, monthIndex: 5 },
    value: dayOf(gregorian, 'ce', 2026, 5, 22),
    today: dayOf(gregorian, 'ce', 2026, 6, 17),
    locale: 'en',
    testid: 'gallery-cdp-gregorian',
  });
}

// ---------- calendarEditor specimen wiring ----------

/** Build the editor's `messages` catalog from the gallery's `t()`. The atom
 * is i18n-decoupled (it ships English defaults in `@dl/ui` and can't import
 * the client's `t()` without a cycle / tripping the English-sweep); the
 * gallery threads its own locale-aware copy here. */
function calendarEditorMessages(): Partial<CalendarEditorMessages> {
  return {
    listEyebrow: t('calendar_editor.list.eyebrow'),
    listTitle: t('calendar_editor.list.title'),
    browsePacks: t('calendar_editor.browse_packs'),
    newCalendar: t('calendar_editor.new_calendar'),
    edit: t('calendar_editor.edit'),
    fork: t('calendar_editor.fork'),
    inspect: t('calendar_editor.inspect'),
    primary: t('calendar_editor.primary'),
    bundled: t('calendar_editor.bundled'),
    pluginPreview: t('calendar_editor.plugin_preview'),
    singlePrimaryTitle: t('calendar_editor.single_primary.title'),
    singlePrimaryBody: t('calendar_editor.single_primary.body'),
    emptyTitle: t('calendar_editor.empty.title'),
    emptyBody: t('calendar_editor.empty.body'),
    backToSimple: t('calendar_editor.back_to_simple'),
    advanced: t('calendar_editor.advanced'),
    cancel: t('calendar_editor.cancel'),
    save: t('calendar_editor.save'),
    done: t('calendar_editor.done'),
    identity: t('calendar_editor.identity'),
    calendarName: t('calendar_editor.calendar_name'),
    months: t('calendar_editor.months'),
    monthsHint: t('calendar_editor.months_hint'),
    renameOnly: t('calendar_editor.rename_only'),
    addMonth: t('calendar_editor.add_month'),
    markIntercalary: t('calendar_editor.mark_intercalary'),
    remove: t('calendar_editor.remove'),
    festival: t('calendar_editor.festival'),
    leapRule: t('calendar_editor.leap_rule'),
    leapNone: t('calendar_editor.leap_none'),
    leapEveryN: t('calendar_editor.leap_every_n'),
    leapJulian: t('calendar_editor.leap_julian'),
    leapGregorian: t('calendar_editor.leap_gregorian'),
    leapEvery: t('calendar_editor.leap_every'),
    leapPhase: t('calendar_editor.leap_phase'),
    leapHint: t('calendar_editor.leap_hint'),
    eras: t('calendar_editor.eras'),
    erasHint: t('calendar_editor.eras_hint'),
    eraName: t('calendar_editor.era_name'),
    abbreviation: t('calendar_editor.abbreviation'),
    startDay: t('calendar_editor.start_day'),
    startDayHelp: t('calendar_editor.start_day_help'),
    yearOne: t('calendar_editor.year_one'),
    yearOneHelp: t('calendar_editor.year_one_help'),
    direction: t('calendar_editor.direction'),
    forward: t('calendar_editor.forward'),
    backward: t('calendar_editor.backward'),
    addEra: t('calendar_editor.add_era'),
    weekdayCycle: t('calendar_editor.weekday_cycle'),
    weekdayHint: t('calendar_editor.weekday_hint'),
    phase: t('calendar_editor.phase'),
    phaseHelp: t('calendar_editor.phase_help'),
    addWeekday: t('calendar_editor.add_weekday'),
    subDay: t('calendar_editor.sub_day'),
    subDayHint: t('calendar_editor.sub_day_hint'),
    addPeriod: t('calendar_editor.add_period'),
    period: t('calendar_editor.period'),
    anchor: t('calendar_editor.anchor'),
    anchorHint: t('calendar_editor.anchor_hint'),
    anchorDay: t('calendar_editor.anchor_day'),
    anchorEvent: t('calendar_editor.anchor_event'),
    livePreview: t('calendar_editor.live_preview'),
    updatesAsYouEdit: t('calendar_editor.updates_as_you_edit'),
    readsToPlayers: t('calendar_editor.reads_to_players'),
    optional: t('calendar_editor.optional'),
    required: t('calendar_editor.required'),
    lengthLocked: t('calendar_editor.length_locked'),
    days: t('calendar_editor.days'),
    errorsBlock: t('calendar_editor.errors_block'),
    noErrors: t('calendar_editor.no_errors'),
  };
}

/** Resolve a validation issue to a localized inline message via `t()`. */
function resolveCalendarIssue(issue: CalendarIssue): string {
  return t(issue.messageKey, issue.params);
}

/** Sample calendars for the list-first manager specimen. Built through `t()`
 * so the gallery source stays English-sweep clean (the names/summaries are
 * user data in the real app, demo content here). */
function calendarListEntries(): readonly CalendarListEntry[] {
  return [
    {
      id: 'cal_reaches',
      name: t('ui_gallery.calendar_editor.sample.reaches_name'),
      summary: t('ui_gallery.calendar_editor.sample.reaches_summary'),
      edited: '2d',
      primary: true,
      source: 'campaign',
    },
    {
      id: 'gregorian',
      name: 'Gregorian',
      summary: t('ui_gallery.calendar_editor.sample.gregorian_summary'),
      source: 'bundled',
    },
    {
      id: 'harptos',
      name: 'Harptos',
      summary: t('ui_gallery.calendar_editor.sample.harptos_summary'),
      source: 'plugin',
    },
  ];
}

/**
 * The LIVE calendarEditor specimen. The host (the standalone gallery) owns
 * the working draft + the simple/advanced mode in its Model; the editor is a
 * stateless atom that emits edit intent through callbacks. Wrapped in a keyed
 * `each` whose key is a digest of the draft so every edit remounts the editor
 * AND its embedded preview picker — the "editor = reader" invariant made
 * visible. Falls back to a static specimen when no host is wired.
 */
function liveCalendarEditor(host: GalleryCdpHost): Mountable {
  const { state, send } = host;
  const messages = calendarEditorMessages();
  const keyOf = (s: GalleryCdpState): string => {
    const w = s.ceWorking;
    const months = w.months.map((m) => `${m.name}:${m.length}:${m.leapLength ?? ''}:${m.intercalary ? 'f' : ''}`).join(',');
    const eras = w.eras.map((e) => `${e.name}:${e.abbreviation ?? ''}:${e.startDay}:${e.yearOne}:${e.direction}`).join(',');
    const wd = (w.weekdays?.labels ?? []).map((x) => x.name).join('|') + ':' + (w.weekdays?.phase ?? '');
    const periods = (w.subDayPeriods ?? []).map((p) => p.name).join('|');
    return `${s.ceMode}#${w.name}#${months}#${w.leapRule.kind}#${eras}#${wd}#${periods}#${w.anchorCanonicalDay}#${w.anchorName ?? ''}`;
  };
  const model = derived([state], (s) => s);
  return each(model.map((s) => [keyOf(s)]), {
    key: (k) => k,
    render: () => {
      const s = model.peek();
      const issues = validateWorkingCalendar(s.ceWorking);
      return [
        calendarEditor({
          mode: s.ceMode,
          working: s.ceWorking,
          issues,
          locale: 'en',
          messages,
          resolveIssue: resolveCalendarIssue,
          testid: 'gallery-calendar-editor',
          onName: (name) => send({ type: 'ce-name', name }),
          onMonth: (index, patch) => send({ type: 'ce-month', index, patch }),
          onAddMonth: () => send({ type: 'ce-add-month' }),
          onRemoveMonth: (index) => send({ type: 'ce-remove-month', index }),
          onToggleIntercalary: (index) => send({ type: 'ce-toggle-intercalary', index }),
          onLeapRule: (rule) => send({ type: 'ce-leap-rule', rule }),
          onEra: (index, patch) => send({ type: 'ce-era', index, patch }),
          onAddEra: () => send({ type: 'ce-add-era' }),
          onRemoveEra: (index) => send({ type: 'ce-remove-era', index }),
          onWeekday: (index, name) => send({ type: 'ce-weekday', index, name }),
          onAddWeekday: () => send({ type: 'ce-add-weekday' }),
          onRemoveWeekday: (index) => send({ type: 'ce-remove-weekday', index }),
          onPhase: (phase) => send({ type: 'ce-phase', phase }),
          onPeriod: (index, name) => send({ type: 'ce-period', index, name }),
          onAddPeriod: () => send({ type: 'ce-add-period' }),
          onRemovePeriod: (index) => send({ type: 'ce-remove-period', index }),
          onAnchorDay: (day) => send({ type: 'ce-anchor-day', day }),
          onAnchorName: (name) => send({ type: 'ce-anchor-name', name }),
          onToggleAdvanced: () => send({ type: 'ce-toggle-advanced' }),
          onCancel: () => undefined,
          onSave: () => undefined,
        }),
      ];
    },
  });
}

/** A static calendarEditor (advanced mode) used when no interactive host is
 * wired (the embedded `/ui-gallery` route). */
function staticCalendarEditor(): Mountable {
  const working = initialWorkingCalendar();
  return calendarEditor({
    mode: 'advanced',
    working,
    issues: validateWorkingCalendar(working),
    locale: 'en',
    messages: calendarEditorMessages(),
    resolveIssue: resolveCalendarIssue,
    testid: 'gallery-calendar-editor',
  });
}

/** The list-first manager specimen (always static — a fixed roster). */
function calendarManagerSpecimen(): Mountable {
  return calendarEditor({
    mode: 'list',
    entries: calendarListEntries(),
    messages: calendarEditorMessages(),
    testid: 'gallery-calendar-manager',
    onOpenEntry: () => undefined,
    onNewCalendar: () => undefined,
    onBrowsePacks: () => undefined,
  });
}

/** The empty-state manager specimen (no calendars). */
function calendarManagerEmptySpecimen(): Mountable {
  return calendarEditor({
    mode: 'list',
    entries: [],
    messages: calendarEditorMessages(),
    testid: 'gallery-calendar-manager-empty',
    onNewCalendar: () => undefined,
  });
}

/** calendarEditor specimens: the list-first manager (+ its empty state) and
 * the LIVE section editor (interactive when a host is wired, else static). */
function calendarEditorSpecimens(host: GalleryCdpHost | undefined): Mountable[] {
  return [
    specimen('ui_gallery.calendar_editor.manager', calendarManagerSpecimen()),
    specimen('ui_gallery.calendar_editor.empty', calendarManagerEmptySpecimen()),
    specimen('ui_gallery.calendar_editor.editor', host ? liveCalendarEditor(host) : staticCalendarEditor()),
  ];
}

/** The calendarDatePicker specimens — one per adaptive case, each shown in
 * the gallery's light + dark stages (the stage chrome handles theming).
 *
 * The FIRST (Gregorian) specimen is INTERACTIVE when a `host` is supplied
 * (the standalone gallery owns the cursor/selection/overlay state); otherwise
 * it renders statically. The remaining four are always static — a fixed
 * cursor renders a populated grid that demonstrates each adaptive case. */
function calendarDatePickerSpecimens(host: GalleryCdpHost | undefined): Mountable[] {
  const gregorian = loadBundledCalendar(GREGORIAN_CALENDAR_ID);
  const harptos = loadBundledCalendar(HARPTOS_CALENDAR_ID);
  const watches = [
    { name: 'Deepnight', time: '00:00' },
    { name: 'Dawn', time: '06:00' },
    { name: 'Highsun', time: '12:00' },
    { name: 'Dusk', time: '18:00' },
  ];
  return [
    // Week-based, forward + backward eras, leap rule (Gregorian) — interactive
    // when a host is wired, else a fixed-cursor static grid.
    specimen('ui_gallery.cdp.gregorian', host ? interactiveGregorian(host) : staticGregorian()),
    // Intercalary festival days interleaved between months (Harptos).
    specimen(
      'ui_gallery.cdp.harptos',
      calendarDatePicker({
        calendar: harptos,
        // Midsummer (month 10) in a leap year → Midsummer + Shieldmeet band.
        value: dayOf(harptos, 'dr', 1492, 10, 1),
        testid: 'gallery-cdp-harptos',
      }),
    ),
    // Fuzzy precision: the granularity segmented control folded INTO the grid
    // (shown at `month`, so the body is the month picker). Switching the toggle
    // is what emits `onGranularity`; this static specimen shows the chrome.
    specimen(
      'ui_gallery.cdp.fuzzy',
      calendarDatePicker({
        calendar: gregorian,
        value: dayOf(gregorian, 'ce', 1482, 4, 1),
        granularity: 'month',
        // Season is dropped automatically (Gregorian models no season cycle);
        // a leading "None" segment demonstrates the clear-to-null option.
        granularities: ['day', 'month', 'season', 'year'],
        granularityClearLabel: 'None',
        messages: galleryDatePickerMessages(),
        testid: 'gallery-cdp-fuzzy',
      }),
    ),
    // Sub-day periods (the time row appears only because we pass them).
    specimen(
      'ui_gallery.cdp.harptos_time',
      calendarDatePicker({
        calendar: harptos,
        value: dayOf(harptos, 'dr', 1492, 1, 11),
        subDayPeriods: watches,
        periodIndex: 2,
        testid: 'gallery-cdp-harptos-time',
      }),
    ),
    // No week cycle → the grid degrades to a numbered day list.
    specimen(
      'ui_gallery.cdp.tidecount',
      calendarDatePicker({
        calendar: TIDECOUNT_CALENDAR,
        value: dayOf(TIDECOUNT_CALENDAR, 'ac', 312, 1, 4),
        testid: 'gallery-cdp-tidecount',
      }),
    ),
    // Backward (BCE-style) era + deep-history negative canonical day.
    specimen(
      'ui_gallery.cdp.deep_history',
      calendarDatePicker({
        calendar: gregorian,
        value: dayOf(gregorian, 'bce', 44, 3, 15),
        testid: 'gallery-cdp-deep-history',
      }),
    ),
  ];
}

function moleculesSection(cdpHost: GalleryCdpHost | undefined): Mountable {
  const calendar = loadBundledCalendar(GREGORIAN_CALENDAR_ID);

  return section(
    { class: 'ui-gallery-group', 'aria-labelledby': 'ui-gallery-molecules-heading' },
    [
      h2({ id: 'ui-gallery-molecules-heading', class: 'ui-gallery-group-heading' }, [
        text(t('ui_gallery.molecules_heading')),
      ]),

      // entityCard — the reusable entity row.
      componentSection('entityCard', 'ui_gallery.entity_card.desc', [
        specimen(
          'entity.root.location',
          entityCard({
            root: 'location',
            name: t('ui_gallery.sample.location_name'),
            meta: t('ui_gallery.sample.location_meta'),
          }),
        ),
        specimen(
          'entity.root.character',
          entityCard({
            root: 'character',
            name: t('ui_gallery.sample.character_name'),
            meta: t('ui_gallery.sample.character_meta'),
            active: true,
            trailing: statusPill({ kind: 'pending', label: statusLabel('pending') }),
          }),
        ),
      ]),

      // validityEditor — interactive when a host is wired (the folded-in date
      // picker + precision toggle are fully editable); falls back to a static
      // read-mode specimen on the embedded route (no host).
      componentSection('validityEditor', 'ui_gallery.validity_editor.desc', [
        specimen(
          'ui_gallery.state.edit',
          cdpHost
            ? liveValidityEditor(cdpHost, 'gallery-validity', 'gallery-validity')
            : validityEditor({
                calendar,
                value: { from: calendar.anchor_canonical_day },
                id: 'gallery-validity',
                mode: 'read',
                onChange: () => {
                  /* gallery specimen — read-only */
                },
              }),
        ),
      ]),

      // calendarDatePicker — the full 7-zone adaptive picker, one specimen
      // per adaptive case (week / no-week / intercalary / leap / era
      // direction / deep history / sub-day). Built-to-design, reviewed.
      componentSection(
        'calendarDatePicker',
        'ui_gallery.calendar_date_picker.desc',
        calendarDatePickerSpecimens(cdpHost),
      ),

      // dateField — the compact field shell that collapses the grid to a
      // trigger + popover. The control wired into the top-bar cursors,
      // game-date facts, and validity bounds. Interactive when a host is
      // wired (reuses the calendarDatePicker's cursor/selection state).
      componentSection('dateField', 'ui_gallery.date_field.desc', [
        specimen(
          'ui_gallery.date_field.interactive',
          cdpHost ? interactiveDateField(cdpHost) : staticDateField(),
        ),
      ]),

      // calendarEditor — authors a CalendarDefinition with a LIVE preview
      // embedding the calendarDatePicker. The list-first manager (+ its empty
      // state) and the section editor: editing a month length re-renders the
      // embedded preview instantly. Built-to-design, reviewed.
      componentSection(
        'calendarEditor',
        'ui_gallery.calendar_editor.desc',
        calendarEditorSpecimens(cdpHost),
      ),

      // assetField — its empty state (no uploader interaction in the gallery).
      componentSection('assetField', 'ui_gallery.asset_field.desc', [
        specimen(
          'ui_gallery.state.empty',
          assetField({
            value: { asset_id: null, url: null },
            id: 'gallery-asset',
            mode: 'read',
            uploader: () => Promise.reject(new Error('gallery specimen')),
            onChange: () => {
              /* gallery specimen — read-only */
            },
          }),
        ),
      ]),

      // ===== §8 molecules — Phase A (UNREVIEWED) =====

      // emptyState — the canonical placeholder, with an action.
      componentSection('emptyState', 'ui_gallery.empty_state.desc', [
        specimen(
          'ui_gallery.state.empty',
          emptyState({
            title: t('ui_gallery.sample.empty_title'),
            message: t('ui_gallery.sample.empty_message'),
            action: uiButton({
              variant: 'primary',
              label: t('ui_gallery.sample.empty_action'),
              testid: 'gallery-empty-action',
              onClick: () => {
                /* gallery specimen — inert */
              },
            }),
            testid: 'gallery-empty-state',
          }),
        ),
      ]),

      // pageHeader + sectionHeading. The pageHeader specimen's <h1> sits
      // after the section <h3>s — heading-order permits going back UP a
      // level, so the gallery's landmark tree stays axe-clean. The
      // sectionHeading specimen uses level 2 (the level it would carry
      // directly under a page <h1> in a real route).
      componentSection(
        'pageHeader',
        'ui_gallery.page_header.desc',
        [
          specimen(
            'ui_gallery.state.default',
            pageHeader({
              title: t('ui_gallery.sample.page_title'),
              intro: t('ui_gallery.sample.page_intro'),
              actions: uiButton({
                variant: 'primary',
                label: t('ui_gallery.sample.page_action'),
                testid: 'gallery-page-header-action',
                onClick: () => {
                  /* gallery specimen — inert */
                },
              }),
            }),
          ),
          specimen(
            'ui_gallery.sample.section_title',
            sectionHeading({
              title: t('ui_gallery.sample.section_title'),
              level: 2,
              actions: uiButton({
                variant: 'ghost',
                label: t('ui_gallery.sample.button_label'),
                onClick: () => {
                  /* gallery specimen — inert */
                },
              }),
            }),
          ),
          // The `overline` variant — a small uppercase sans grouping rail
          // (the mock's `.section-h`). Same level (2) so heading-order
          // stays valid alongside the title specimen above.
          specimen(
            'ui_gallery.state.overline',
            sectionHeading({
              title: t('ui_gallery.sample.overline_title'),
              level: 2,
              variant: 'overline',
              testId: 'gallery-section-heading-overline',
            }),
          ),
        ],
      ),
    ],
  );
}

// ---------- Editing surfaces (complex fact / trait authoring UIs) ----------

/** Value-kind options for a tuple sub-field (no nested tuples / prose). */
function tupleKindOptions(): { value: string; label: string }[] {
  return [
    { value: 'text', label: t('trait_editor.kind.text') },
    { value: 'number', label: t('trait_editor.kind.number') },
    { value: 'boolean', label: t('trait_editor.kind.boolean') },
    { value: 'enum', label: t('trait_editor.kind.enum') },
    { value: 'ref', label: t('trait_editor.kind.ref') },
  ];
}

/**
 * Gallery-local validation preview — mirrors the essentials of the app's
 * `buildTraitDefinitionOps` (a name, ≥1 field, every field named, enum fields
 * have choices, tuple fields have named sub-fields) WITHOUT importing the app's
 * value (which would drag all of app.ts into the gallery bundle). Returns the
 * predicate count it would emit, or the first blocking error. */
function traitBuildPreview(te: TraitEditorState): { ok: true; count: number } | { ok: false; error: string } {
  if (te.traitId.trim() === '') return { ok: false, error: t('ui_gallery.trait_build.err_trait') };
  if (te.fields.length === 0) return { ok: false, error: t('ui_gallery.trait_build.err_fields') };
  for (const f of te.fields) {
    if (f.id.trim() === '') return { ok: false, error: t('ui_gallery.trait_build.err_name') };
    if (f.valueKind === 'enum' && (f.enumValues ?? '').split(',').filter((c) => c.trim() !== '').length === 0)
      return { ok: false, error: t('ui_gallery.trait_build.err_enum') };
    if (f.valueKind === 'tuple') {
      const subs = f.tupleFields ?? [];
      if (subs.length === 0) return { ok: false, error: t('ui_gallery.trait_build.err_tuple') };
      for (const s of subs) {
        if (s.name.trim() === '') return { ok: false, error: t('ui_gallery.trait_build.err_tuple_name') };
        if (s.valueKind === 'enum' && (s.enumValues ?? '').split(',').filter((c) => c.trim() !== '').length === 0)
          return { ok: false, error: t('ui_gallery.trait_build.err_enum') };
      }
    }
  }
  return { ok: true, count: te.fields.length };
}

/** One tuple sub-field row in the live trait editor. */
function tupleSubRow(
  sub: TupleEditorField,
  field: number,
  j: number,
  dispatch: (m: GalleryCdpMsg) => void,
): Mountable {
  return div({ class: 'ui-gallery-te-tuple-row', 'data-testid': 'gallery-te-tuple-row' }, [
    textInput({
      value: sub.name,
      ariaLabel: t('ui_gallery.trait_build.sub_name'),
      onInput: (v: string) => dispatch({ type: 'te-set-tuple', field, sub: j, patch: { name: v } }),
    }),
    selectInput({
      id: `gx-te-tuple-kind-${field}-${j}`,
      value: sub.valueKind,
      ariaLabel: t('ui_gallery.trait_field.kind'),
      options: tupleKindOptions(),
      onChange: (v: string) => dispatch({ type: 'te-set-tuple', field, sub: j, patch: { valueKind: v as TupleFieldKind } }),
    }),
    ...(sub.valueKind === 'enum'
      ? [
          textInput({
            value: sub.enumValues ?? '',
            ariaLabel: t('ui_gallery.trait_field.choices'),
            onInput: (v: string) => dispatch({ type: 'te-set-tuple', field, sub: j, patch: { enumValues: v } }),
          }),
        ]
      : []),
    label({ class: 'ui-gallery-te-optional' }, [
      input({
        type: 'checkbox',
        checked: sub.optional,
        onChange: (e: Event) =>
          dispatch({ type: 'te-set-tuple', field, sub: j, patch: { optional: (e.target as HTMLInputElement).checked } }),
      }),
      span([text(t('ui_gallery.trait_build.optional'))]),
    ]),
    uiButton({
      variant: 'ghost',
      label: t('ui_gallery.trait_build.remove'),
      ariaLabel: t('ui_gallery.trait_build.remove_sub'),
      onClick: () => dispatch({ type: 'te-remove-tuple', field, sub: j }),
    }),
  ]);
}

/** One predicate field row in the live trait editor. */
function traitFieldRow(f: TraitEditorField, index: number, dispatch: (m: GalleryCdpMsg) => void): Mountable {
  return div({ class: 'ui-gallery-te-field', 'data-testid': 'gallery-te-field' }, [
    div({ class: 'ui-gallery-te-field-head' }, [
      textInput({
        value: f.id,
        testid: 'gallery-te-field-name',
        ariaLabel: t('ui_gallery.trait_field.name'),
        onInput: (v: string) => dispatch({ type: 'te-set-field', index, patch: { id: v } }),
      }),
      selectInput({
        id: `gx-te-kind-${index}`,
        value: f.valueKind,
        testid: 'gallery-te-field-kind',
        ariaLabel: t('ui_gallery.trait_field.kind'),
        options: editingValueKindOptions(),
        onChange: (v: string) => dispatch({ type: 'te-set-field', index, patch: { valueKind: v as ValueKind } }),
      }),
      selectInput({
        id: `gx-te-card-${index}`,
        value: f.cardinality,
        ariaLabel: t('ui_gallery.trait_field.cardinality'),
        options: [
          { value: 'single', label: t('ui_gallery.trait_field.single') },
          { value: 'many', label: t('ui_gallery.trait_field.many') },
        ],
        onChange: (v: string) =>
          dispatch({ type: 'te-set-field', index, patch: { cardinality: v === 'many' ? 'many' : 'single' } }),
      }),
      uiButton({
        variant: 'ghost',
        label: t('ui_gallery.trait_build.remove'),
        ariaLabel: t('ui_gallery.trait_build.remove_field'),
        onClick: () => dispatch({ type: 'te-remove-field', index }),
      }),
    ]),
    // Enum choices (kind = enum).
    ...(f.valueKind === 'enum'
      ? [
          textInput({
            value: f.enumValues ?? '',
            ariaLabel: t('ui_gallery.trait_field.choices'),
            onInput: (v: string) => dispatch({ type: 'te-set-field', index, patch: { enumValues: v } }),
          }),
        ]
      : []),
    // Structured-record sub-fields (kind = tuple).
    ...(f.valueKind === 'tuple'
      ? [
          div({ class: 'ui-gallery-te-tuple' }, [
            ...(f.tupleFields ?? []).map((s, j) => tupleSubRow(s, index, j, dispatch)),
            uiButton({
              variant: 'ghost',
              label: t('trait_editor.tuple_field_add'),
              testid: 'gallery-te-add-tuple',
              onClick: () => dispatch({ type: 'te-add-tuple', field: index }),
            }),
          ]),
        ]
      : []),
  ]);
}

/** Build the trait-editor form from a plain snapshot — eager DOM (no signals
 * inside; the live wrapper remounts it on every edit). `dispatch` is null in
 * the static (no-host) fallback, making the controls inert. */
function traitEditorForm(te: TraitEditorState, send: Send<GalleryCdpMsg> | null): Mountable {
  const dispatch = (m: GalleryCdpMsg): void => {
    if (send) send(m);
  };
  const preview = traitBuildPreview(te);
  return div({ class: 'ui-gallery-trait-editor', 'data-testid': 'gallery-trait-editor' }, [
    formField({
      id: 'gallery-te-name-field',
      label: t('trait_editor.trait_id_label'),
      children: [
        textInput({
          value: te.traitId,
          testid: 'gallery-te-name',
          ariaLabel: t('trait_editor.trait_id_label'),
          attrs: { id: 'gallery-te-name-field' },
          onInput: (v: string) => dispatch({ type: 'te-name', name: v }),
        }),
      ],
    }),
    ...te.fields.map((f, index) => traitFieldRow(f, index, dispatch)),
    uiButton({
      variant: 'secondary',
      label: t('trait_editor.add_field'),
      testid: 'gallery-te-add-field',
      onClick: () => dispatch({ type: 'te-add-field' }),
    }),
    div(
      {
        class: 'ui-gallery-te-preview',
        'data-testid': 'gallery-te-preview',
        'data-ok': preview.ok ? 'true' : 'false',
      },
      [
        span({ class: 'ui-gallery-te-preview-label' }, [text(t('ui_gallery.trait_build.label'))]),
        text(
          preview.ok
            ? t('ui_gallery.trait_build.ok', { n: preview.count })
            : preview.error,
        ),
      ],
    ),
  ]);
}

/** The LIVE trait editor — interactive when a host is wired (keyed `each`
 * remounts the form on every edit, mirroring `liveCalendarEditor`). */
function liveTraitEditor(host: GalleryCdpHost): Mountable {
  const { state, send } = host;
  const keyOf = (s: GalleryCdpState): string => {
    const te = s.teState;
    const fields = te.fields
      .map((f) => {
        const subs = (f.tupleFields ?? []).map((x) => `${x.name}:${x.valueKind}:${x.optional ? 'o' : ''}:${x.enumValues ?? ''}`).join('|');
        return `${f.key}~${f.id}:${f.valueKind}:${f.cardinality}:${f.enumValues ?? ''}:${subs}`;
      })
      .join(',');
    return `${te.traitId}#${te.nextFieldKey}#${fields}`;
  };
  const model = derived([state], (s) => s);
  return each(model.map((s) => [keyOf(s)]), {
    key: (k) => k,
    render: () => {
      const s = model.peek();
      return [traitEditorForm(s.teState, send)];
    },
  });
}

/** Static trait-editor form for the no-host route (controls inert). */
function staticTraitEditor(): Mountable {
  return traitEditorForm(initialTraitEditorState(), null);
}

/**
 * The LIVE fact-row editor — drives the REAL generic `viewEntityFactRows`
 * (the same renderer the entity page uses) over a gallery-owned mock entity
 * carrying one fact per value-kind. Editing a value + Save updates it, clearing
 * a value soft-deletes the fact, the in-world date field opens the calendar
 * grid — all reactive through the gallery's `fr-*` reducer. `viewEntityFactRows`
 * is itself reactive (it wires its own bindings to the live `state` signal), so
 * unlike the stateless atoms it is rendered DIRECTLY, not snapshotted in a
 * keyed `each`. The mock vocabulary is injected via `setHomebrewDefs` in
 * `gallery-main.ts` (standalone only). */
function liveFactRowEditor(host: GalleryCdpHost): Mountable {
  const { state, send } = host;
  return div(
    { class: 'gallery-factrow-live', 'data-testid': 'gallery-factrow' },
    viewEntityFactRows(state, send, GX_KEEP, galleryFactRowHost()) as Mountable[],
  );
}

/** The value-kind options shown by the trait/fact editors' kind picker. */
function editingValueKindOptions(): { value: string; label: string }[] {
  return [
    { value: 'text', label: t('trait_editor.kind.text') },
    { value: 'rich_text', label: t('trait_editor.kind.rich_text') },
    { value: 'number', label: t('trait_editor.kind.number') },
    { value: 'boolean', label: t('trait_editor.kind.boolean') },
    { value: 'enum', label: t('trait_editor.kind.enum') },
    { value: 'ref', label: t('trait_editor.kind.ref') },
    { value: 'tuple', label: t('trait_editor.kind.tuple') },
  ];
}

/** A tuple sub-field cell, mirroring the fact-row tuple layout
 * (`.fact-tuple-field` + `.fact-sub-label`, styled by the app stylesheet the
 * gallery imports). */
function tupleFieldCell(name: string, required: boolean, control: Mountable): Mountable {
  return div({ class: 'fact-tuple-field' }, [
    label({ class: 'fact-sub-label' }, [text(name + (required ? ' *' : ''))]),
    control,
  ]);
}

/**
 * "Editing surfaces" — the complex authoring UIs (per Franco's request to show
 * the fact field editors, the trait field editor, and other complex surfaces).
 *
 * The fact-field + trait-field specimens render the REAL `@dl/ui` input atoms
 * the live editors compose (`textInput`, `selectInput`, `dateField`, the
 * `markdown()` renderer, the `.fact-tuple` / `.fact-boolean` chrome) with
 * representative values — a faithful, axe-scannable showcase of each value
 * kind's control. The fully host-wired fact-row + trait-editor forms stay in
 * the app (they need a live entity/registry substrate); these specimens are the
 * controls those forms mount. The `validityEditor` specimen is the real atom in
 * write mode, surfacing the precision selector + open-ended toggles. Flagged
 * UNREVIEWED pending design sign-off. */
function editingSurfacesSection(cdpHost: GalleryCdpHost | undefined): Mountable {
  const calendar = loadBundledCalendar(GREGORIAN_CALENDAR_ID);
  const anchor = calendar.anchor_canonical_day;
  return section(
    { class: 'ui-gallery-group', 'aria-labelledby': 'ui-gallery-editing-heading' },
    [
      h2({ id: 'ui-gallery-editing-heading', class: 'ui-gallery-group-heading' }, [
        text(t('ui_gallery.editing_heading')),
      ]),

      // Fact field editors — the LIVE entity fact-row editor (the real
      // `viewEntityFactRows`) when a host is wired (standalone gallery):
      // editing a value + Save updates it, clearing soft-deletes the fact, the
      // in-world date field opens the calendar grid, the structured-record
      // tuple authors inline. Falls back to one static control per value-kind
      // on the embedded `/ui-gallery` route (which has no host).
      componentSection(
        'factFieldEditor',
        'ui_gallery.fact_field.desc',
        cdpHost
          ? [specimen('ui_gallery.state.edit', liveFactRowEditor(cdpHost))]
          : [
          specimen(
            'ui_gallery.fact_field.text',
            textInput({
              value: 'Stormhold Keep',
              ariaLabel: t('ui_gallery.fact_field.text'),
              onInput: () => undefined,
            }),
          ),
          specimen(
            'ui_gallery.fact_field.number',
            textInput({
              type: 'number',
              value: '12000',
              ariaLabel: t('ui_gallery.fact_field.number'),
              onInput: () => undefined,
            }),
          ),
          specimen(
            'ui_gallery.fact_field.rich_text',
            div({ class: 'markdown-body' }, [
              markdown('A **towering** basalt fortress with *silver* gates.\n\n- garrisoned\n- warded'),
            ]),
          ),
          specimen(
            'ui_gallery.fact_field.enum',
            selectInput({
              id: 'gx-ff-enum',
              value: 'monarchy',
              ariaLabel: t('ui_gallery.fact_field.enum'),
              options: [
                { value: 'monarchy', label: 'Monarchy' },
                { value: 'council', label: 'Council' },
                { value: 'theocracy', label: 'Theocracy' },
              ],
              onChange: () => undefined,
            }),
          ),
          specimen(
            'ui_gallery.fact_field.ref',
            selectInput({
              id: 'gx-ff-ref',
              value: 'aria',
              ariaLabel: t('ui_gallery.fact_field.ref'),
              options: [
                { value: 'aria', label: 'Queen Aria' },
                { value: 'aldric', label: 'Ser Aldric' },
              ],
              onChange: () => undefined,
            }),
          ),
          specimen(
            'ui_gallery.fact_field.boolean',
            label({ class: 'fact-boolean' }, [
              input({ type: 'checkbox', checked: true }),
              span({ class: 'fact-boolean-state' }, [text('Walled')]),
            ]),
          ),
          specimen('ui_gallery.fact_field.date', staticDateField()),
          specimen(
            'ui_gallery.fact_field.tuple',
            div(
              { class: 'fact-tuple', role: 'group', 'aria-label': t('ui_gallery.fact_field.tuple') },
              [
                tupleFieldCell(
                  'Role',
                  true,
                  selectInput({
                    id: 'gx-ff-tuple-role',
                    value: 'warden',
                    ariaLabel: 'Role',
                    options: [
                      { value: 'warden', label: 'Warden' },
                      { value: 'steward', label: 'Steward' },
                    ],
                    onChange: () => undefined,
                  }),
                ),
                tupleFieldCell(
                  'Holder',
                  true,
                  selectInput({
                    id: 'gx-ff-tuple-holder',
                    value: 'aria',
                    ariaLabel: 'Holder',
                    options: [{ value: 'aria', label: 'Queen Aria' }],
                    onChange: () => undefined,
                  }),
                ),
                tupleFieldCell(
                  'Note',
                  false,
                  textInput({
                    value: t('ui_gallery.fact_field.tuple_note_sample'),
                    ariaLabel: 'Note',
                    onInput: () => undefined,
                  }),
                ),
              ],
            ),
          ),
        ],
        false,
      ),

      // Trait editor — the LIVE per-predicate authoring form. Interactive when a
      // host is wired (standalone gallery): renaming the trait, adding/removing
      // fields, switching a value-kind, editing enum choices, and authoring
      // structured-record sub-fields all update the form + the build preview
      // instantly. Falls back to a static (inert) form on the embedded route.
      componentSection(
        'traitEditor',
        'ui_gallery.trait_field.desc',
        [
          specimen(
            'ui_gallery.state.edit',
            cdpHost ? liveTraitEditor(cdpHost) : staticTraitEditor(),
          ),
        ],
        false,
      ),

      // Validity editor — WRITE mode (the read-mode specimen is in Molecules).
      // Surfaces the from/to bounds, the open-ended toggles, and the precision
      // selector (the styled captioned field).
      componentSection(
        'validityEditor (write)',
        'ui_gallery.validity_write.desc',
        [
          specimen(
            'ui_gallery.state.edit',
            cdpHost
              ? liveValidityEditor(cdpHost, 'gx-validity-write', 'gallery-validity-write')
              : validityEditor({
                  calendar,
                  value: { from: anchor, to: anchor + 400, from_precision: 'year' },
                  id: 'gx-validity-write',
                  mode: 'edit',
                  onChange: () => undefined,
                }),
          ),
        ],
        false,
      ),
    ],
  );
}

/**
 * `/ui-gallery` route view. Generic over `Model, Msg` so it mounts into the
 * app component like the other static route views (`viewA11yPage`) — the bulk
 * of the content does not read the model.
 *
 * The ONE stateful specimen — the interactive Gregorian calendar picker — is
 * host-driven: pass `cdpHost` (a `{ state, send }` over the gallery's CDP
 * slice) to make it interactive. The standalone gallery (`gallery-main.ts`)
 * owns that slice and passes it; the embedded `/ui-gallery` route in `app.ts`
 * omits it, and the specimen renders statically.
 */
export function viewUiGallery<Model, Msg>(
  _state: Signal<Model>,
  _send: Send<Msg>,
  cdpHost?: GalleryCdpHost,
): Mountable {
  return main(
    { role: 'main', 'data-testid': 'ui-gallery-page', class: 'ui-gallery-page' },
    [
      h1({ class: 'ui-gallery-title' }, [text(t('ui_gallery.title'))]),
      p({ class: 'ui-gallery-intro' }, [text(t('ui_gallery.intro'))]),
      atomsSection(),
      moleculesSection(cdpHost),
      editingSurfacesSection(cdpHost),
    ],
  );
}
